// Import style
import './style.scss';

// Import utilities
import { html, render } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';

import { parseDynamicSentence } from '@builder-legacy/utilities';
import { getIntegrations } from '@builder-legacy/integrations/utilities';
import { getConditionsByIntegration } from '@builder-legacy/item/conditions/utilities';
import { isEveryoneRecipeWithoutUserData } from '@builder-legacy/recipe/utilities';
import { promptSetDataDialog } from '@builder-legacy/user-selector/utilities';

const { isDefined } = _uo.utility;

// Import components
import ItemConditionsModalOptions from '@builder-legacy/item/conditions/modal-options';

/**
 * Creates a modal with the list of conditions
 */
export default class ItemConditionsModalList {
	/**
	 * Creates a reference of the dialog
	 *
	 * @type {Ref}
	 */
	$dialog = createRef();

	/**
	 * Invokes the main methods that create the modal
	 *
	 * @param {Object}  options                   The options
	 * @param {Integer} options.conditionsGroupId The ID of the conditions group
	 * @param {String}  options.comingFrom        A string that identifies if this modal was created
	 *                                            using the "Filter" button, or the "+ Add" button
	 * @return {undefined}
	 */
	constructor( { conditionsGroupId, comingFrom, parentID } ) {
		// Save item ID
		this.conditionsGroupId = conditionsGroupId;

		// Save "coming from"
		// This will basically tell us if the user created this modal by clicking on the "Filter
		// button in actions, or if they clicked on the "+Add" button in the conditions block
		this.comingFrom = comingFrom;

		// Save parent ID
		this.parentID = parentID;

		// Checks if the filter is inside a user loop
		this.isInsideUserLoop = UncannyAutomator._core.recipe.item.loop.isItemInsideUserLoop( this.parentID );

		// Render
		this.render();
	}

	/**
	 * Renders the dialog with the list
	 *
	 * @return {undefined}
	 */
	render() {

		render(
			html`
				<uo-dialog
					heading="${ UncannyAutomator._core.i18n.conditions.condition }"
					size="large"
					open

					${ ref( this.$dialog ) }
				>
					<div class="uap-conditions-list">
						<uo-accordion
							list-search
							
							.searchPlaceholder="${ UncannyAutomator._core.i18n.conditions.searchConditions }"
							.noResultsText="${ UncannyAutomator._core.i18n.conditions.noConditionsFound }"
						>

							${
								// Lit's repeat. If you need more information, check
								// https://lit.dev/docs/templates/directives/#repeat
								repeat(
									// Get integrations with conditions
									this.integrations,
									// eslint-disable-next-line no-unused-vars
									( [ integrationCode, integration ] ) => integrationCode,
									( [ integrationCode, integration ] ) => html`
										<uo-accordion-item
											?open=${ this.integrationOpenByDefault( integrationCode ) }
										>

											<div slot="summary">
												<div class="uap-conditions-list-item-integration">
													<ua-builder-integration-icon
														code="${ integrationCode }"
														size="xsmall"
														plain
													></ua-builder-integration-icon>

													${ integration.name }
												</div>
											</div>

											${
												repeat(
													// Don't show deprecated conditions
													getConditionsByIntegration( {
														integrationCode: integrationCode,
														filters: { deprecated: false }
													} ),
													// eslint-disable-next-line no-unused-vars
													( condition ) => condition.id,
													( condition ) => {
														// The condition MIGHT require user data only if it's not inside a user loop
														const requiresUserData = ! this.isInsideUserLoop && condition.requires_user;

														return html`
															<uo-accordion-item-li
																@click=${ () => {

																	// Select the condition
																	this.selectCondition( {
																		// Condition identifiers
																		integrationCode: integrationCode,
																		conditionCode: condition.id,
																		name: condition.name,
																		requiresUserData: requiresUserData
																	} );

																} }
															>

																<div class="uap-conditions-list-item-sentence">
																	${ parseDynamicSentence( { dynamicSentence: condition.name } ) }
																</div>

																${ this._templateAddRequiresUserData( { requiresUserData: requiresUserData } ) }

															</uo-accordion-item-li>
														`;
													}
												)
											}

										</uo-accordion-item>
									`
								)
							}

						</uo-accordion>
					</div>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ UncannyAutomator._core.i18n.cancel }
					</uo-button>
				</uo-dialog>
			`,
			this.$wrapper,
			{ host: this }
		);

	}

	/**
	 * Creates the modal with the condition options
	 *
	 * @param {Object}  options                  The options
	 * @param {String}  options.integrationCode  The unique code of the integration
	 * @param {String}  options.conditionCode    The unique code of the condition
	 * @param {String}  options.name             The condition name
	 * @param {Boolean} options.requiresUserData True if the condition we're evaluating requires user data
	 * @return {undefined}
	 */
	async selectCondition( { integrationCode, conditionCode, name, requiresUserData } ) {
		// Check if this condition requires user data first
		if ( requiresUserData && isEveryoneRecipeWithoutUserData() ) {
			try {
				// Request the user to enter the user data
				await promptSetDataDialog( {
					summary: UncannyAutomator._core.i18n.conditions.conditionRequiresUserData,
					box: parseDynamicSentence( { dynamicSentence: name } ),
					description: UncannyAutomator._core.i18n.conditions.conditionRequiresUserDataDescription,
				} );
			} catch ( e ) {
				// Stop here, go back
				return;
			}
		}

		// Close the dialog
		this.$dialog.value.close();

		// Trigger custom event
		const event = new CustomEvent( 'automator/action/conditions/add/select', {
			detail: {
				conditionsGroupId: this.conditionsGroupId,

				// Condition identifiers
				integrationCode: integrationCode,
				conditionCode: conditionCode,
			},
		} );

		// Dispatch event
		document.dispatchEvent( event );

		// Create modal
		new ItemConditionsModalOptions( {
			conditionsGroupId: this.conditionsGroupId,

			// Condition identifiers
			integrationCode: integrationCode,
			conditionCode: conditionCode,

			// Set the source
			comingFrom: this.comingFrom,
		} );
	}

	/**
	 * Checks whether an item needs the "Requires user data" chip and crates the template
	 *
	 * @param {Object}  options                  The options
	 * @param {Boolean} options.requiresUserData True if the condition we're evaluating requires user data
	 * @return {TemplateResult} The template with the chip
	 */
	_templateAddRequiresUserData( { requiresUserData } ) {
		// Check if this we should add the tag
		if ( requiresUserData && isEveryoneRecipeWithoutUserData() ) {

			return html`
				<uo-chip
					slot="chip"
					size="small"
					color="info"
					filled
				>
					${ UncannyAutomator._core.i18n.conditions.requiresUserData }
				</uo-chip>
			`;

		}
	}

	/**
	 * Determines whether an integration should be open by default
	 *
	 * @param {String} integrationCode The unique code of the integration
	 * @return {Boolean}                True if this integration should be open by default
	 */
	integrationOpenByDefault( integrationCode ) {
		return integrationCode == 'GEN';
	}

	/**
	 * Returns a sorted list of integrations
	 *
	 * @return {Array} The list of integrations
	 */
	get integrations() {
		// Get the integrations with conditions
		const items = Object.entries( getIntegrations( { conditions: true } ) );

		// Get the index of the "General" integration
		// eslint-disable-next-line no-unused-vars
		const index = items.findIndex( ( [ integrationCode, integration ] ) => {
			return integrationCode == 'GEN';
		} );

		// Move the "General" integration to the first position
		items.unshift( items.splice( index, 1 )[ 0 ] );

		return items;
	}

	/**
	 * The wrapper where we're going to add the dialog
	 * We're going to use this until the whole UI moves to Lit
	 *
	 * @return {Node} A wrapper
	 */
	get $wrapper() {
		// Check if we already created one
		if ( ! isDefined( this.#$wrapper ) ) {
			// Create the element
			this.#$wrapper = document.createElement( 'div' );

			// Append it to the DOM
			document.body.append( this.#$wrapper );
		}

		// Return the wrapper
		return this.#$wrapper;
	}

	#$wrapper;
}
